import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";

export type NotifySeverity = "success" | "info" | "warning" | "error";
export type Notice = { message: string; severity?: NotifySeverity; durationMs?: number };
export type Notify = (notice: Notice) => void;

type Queued = Notice & { key: number };

const NotifyContext = createContext<Notify>(() => {});

export function SnackbarProvider({ children }: { children?: ReactNode }) {
  const [queue, setQueue] = useState<Queued[]>([]);
  const [open, setOpen] = useState(true);
  const current = queue[0];

  const notify = useCallback<Notify>((notice) => {
    // Não reabre aqui: um aviso dispensado em transição de saída não pode voltar; o onExited abre o próximo.
    setQueue((q) => [...q, { ...notice, key: Date.now() + Math.random() }]);
  }, []);

  const close = (_event?: unknown, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };
  const next = () => {
    setQueue((q) => q.slice(1));
    setOpen(true);
  };

  const value = useMemo(() => notify, [notify]);
  const severity = current?.severity || "success";

  return (
    <NotifyContext.Provider value={value}>
      {children}
      <Snackbar
        key={current?.key}
        open={Boolean(current) && open}
        autoHideDuration={current?.durationMs ?? (severity === "error" ? 5000 : 4500)}
        onClose={close}
        TransitionProps={{ onExited: next }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {current ? (
          <Alert severity={severity} onClose={close} variant="standard" sx={{ width: "100%", boxShadow: 3 }}>
            {current.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </NotifyContext.Provider>
  );
}

export function useNotify(): Notify {
  return useContext(NotifyContext);
}
